import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CameraCapture } from "@/components/CameraCapture";
import { ChecklistFill } from "@/components/ChecklistFill";
import { StatusBadge } from "@/components/StatusBadge";
import { stripBase64, compressImage, fileToDataUrl } from "@/lib/imageUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Loader2, ArrowLeft, ScanLine, CheckCircle2, XCircle, Camera, ShieldAlert,
  QrCode, Send, Radio, Wifi, Play, Pause, Square, X, Clock, AtSign, Paperclip, UserCheck,
  FileText, Download, Upload, Plus, ClipboardList,
} from "lucide-react";

function fmtDuration(sec) {
  sec = sec || 0;
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function Step({ n, title, done, active, children }) {
  return (
    <Card className={`border-slate-200 bg-white p-5 shadow-sm ${active ? "ring-1 ring-blue-400" : ""}`}>
      <div className="mb-3 flex items-center gap-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-black ${done ? "bg-emerald-500 text-white" : active ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
          {done ? <CheckCircle2 className="h-4 w-4" /> : n}
        </div>
        <h3 className="font-heading font-bold text-slate-900">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

export default function InstallationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const [inst, setInst] = useState(null);
  const [scan, setScan] = useState(null);
  const [busResult, setBusResult] = useState(null);
  const [prodResult, setProdResult] = useState(null);
  const [manualBus, setManualBus] = useState("");
  const [prodSerialInput, setProdSerialInput] = useState("");
  const [prodName, setProdName] = useState("");
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [ttl, setTtl] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [tick, setTick] = useState(0);
  // comments
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [mentionable, setMentionable] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);
  const [projectForms, setProjectForms] = useState([]);
  const [pendingDoc, setPendingDoc] = useState(null); // { dataUrl, file_name }
  const [docFormId, setDocFormId] = useState("");
  const [docLabel, setDocLabel] = useState("");
  const [checklistForm, setChecklistForm] = useState(null);
  const docFileRef = useRef(null);

  const load = useCallback(() => api.get(`/installations/${id}`).then((r) => setInst(r.data)), [id]);
  const loadComments = useCallback(() => api.get(`/installations/${id}/comments`).then((r) => setComments(r.data)), [id]);
  useEffect(() => { load(); loadComments(); api.get("/users/mentionable").then((r) => setMentionable(r.data)); }, [load, loadComments]);
  useEffect(() => {
    if (inst?.project_id) api.get(`/projects/${inst.project_id}/forms`).then((r) => setProjectForms(r.data)).catch(() => {});
  }, [inst?.project_id]);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    if (ttl <= 0) return;
    const t = setInterval(() => setTtl((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [ttl]);

  useEffect(() => {
    if (!inst?.timer_running) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [inst?.timer_running]);

  if (!inst)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const liveElapsed = (inst.elapsed_seconds || 0) + (inst.timer_running ? tick : 0);
  const inProjectPool = (inst.project_technician_ids || []).includes(user.id);

  const verify = async (which, image_base64, manual_value) => {
    setBusy(true);
    try {
      const ep = which === "bus" ? "verify-bus" : "verify-product";
      const { data } = await api.post(`/installations/${id}/${ep}`, {
        image_base64: image_base64 ? stripBase64(image_base64) : null,
        manual_value: manual_value || null,
        method: manual_value ? "MANUAL" : "SCAN",
      });
      if (which === "bus") setBusResult(data); else setProdResult(data);
      if (data.matched) { toast.success(which === "bus" ? t("installDetail.busMatches") : t("installDetail.productMatches")); load(); }
      else toast.error(t("installDetail.noMatch", { read: data.extracted || "—", expected: data.expected }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setScan(null); }
  };

  const scanProductSerial = async (dataUrl) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ocr/scan`, { image_base64: stripBase64(dataUrl), hint: "product barcode / serial number sticker" });
      if (data.extracted) { setProdSerialInput(data.extracted); toast.success(t("installDetail.scanned", { value: data.extracted })); }
      else toast.error(t("installDetail.cantRead"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setScan(null); }
  };

  const saveProductSerial = async () => {
    if (!prodSerialInput.trim()) return toast.error(t("installDetail.scanTypeFirst"));
    setBusy(true);
    try {
      const { data } = await api.post(`/installations/${id}/product-serials/add`, { serial: prodSerialInput.trim(), product_name: prodName.trim() || null, method: "MANUAL" });
      toast.success(t("installDetail.serialRecorded"));
      setProdSerialInput(""); setProdName("");
      setInst((prev) => ({ ...prev, product_serials: data.product_serials, product_verified: true }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeProductSerial = async (serial_id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/installations/${id}/product-serials/remove`, { serial_id });
      setInst((prev) => ({ ...prev, product_serials: data.product_serials, product_verified: data.product_serials.length > 0 }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const capturePhoto = async (which, dataUrl) => {
    setBusy(true);
    try {
      await api.post(`/installations/${id}/photos/add`, { which, image_base64: dataUrl });
      toast.success(t("installDetail.photoAdded", { which: which === "before" ? t("installDetail.before") : t("installDetail.after") })); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setScan(null); }
  };

  const removePhoto = async (which, path) => {
    setBusy(true);
    try {
      await api.post(`/installations/${id}/photos/remove`, { which, path });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDocFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await fileToDataUrl(f);
    setPendingDoc({ dataUrl, file_name: f.name });
    setDocFormId("");
    setDocLabel("");
    if (docFileRef.current) docFileRef.current.value = "";
  };

  const confirmDocUpload = async () => {
    if (!docFormId) return toast.error(t("installDetail.selectDocType"));
    if (docFormId === "OTHERS" && !docLabel.trim()) return toast.error(t("installDetail.enterDocLabel"));
    setBusy(true);
    try {
      await api.post(`/installations/${id}/documents/add`, {
        file_base64: pendingDoc.dataUrl,
        file_name: pendingDoc.file_name,
        form_id: docFormId === "OTHERS" ? null : docFormId,
        label: docFormId === "OTHERS" ? docLabel.trim() : null,
      });
      toast.success(t("installDetail.documentUploaded"));
      setPendingDoc(null); setDocFormId(""); setDocLabel("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeDocument = async (doc_id) => {
    setBusy(true);
    try { await api.post(`/installations/${id}/documents/remove`, { doc_id }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const timer = async (action) => {
    setBusy(true);
    try { const { data } = await api.post(`/installations/${id}/timer/${action}`); setTick(0); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selfAssign = async () => {
    try { await api.post(`/installations/${id}/self-assign`); toast.success(t("installDetail.busAssigned")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const requestOverride = async () => {
    await api.post(`/installations/${id}/override-request`);
    toast.info(t("installDetail.overrideSent")); load();
  };

  const submit = async (route) => {
    setBusy(true);
    try {
      if (route === "ONSITE") {
        const { data } = await api.post(`/installations/${id}/qr-token`);
        setQr(`${window.location.origin}/verify/qr/${data.token}`); setTtl(data.ttl);
        await api.post(`/installations/${id}/submit`, { route: "ONSITE" });
      } else {
        await api.post(`/installations/${id}/submit`, { route: "REMOTE" });
        toast.success(t("installDetail.sentRemote"));
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addMention = (u) => {
    if (!mentions.find((m) => m.id === u.id)) setMentions([...mentions, u]);
    setCommentText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}@${u.name} `);
  };
  const onAttach = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const raw = await fileToDataUrl(f); setAttachment(await compressImage(raw));
  };
  const postComment = async () => {
    if (!commentText.trim() && !attachment) return;
    setBusy(true);
    try {
      await api.post(`/installations/${id}/comments`, { text: commentText, attachment, mentions: mentions.map((m) => m.id) });
      setCommentText(""); setMentions([]); setAttachment(null);
      loadComments();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const busDone = inst.bus_verified, prodDone = inst.product_verified;
  const photosDone = (inst.photos_before?.length > 0) && (inst.photos_after?.length > 0);
  const mandatoryForms = projectForms.filter((f) => f.mandatory);
  const checklistForms = projectForms.filter((f) => f.form_type === "checklist");
  const uploadForms = projectForms.filter((f) => (f.form_type || "upload") === "upload");
  const uploadedFormIds = new Set((inst.documents || []).map((d) => d.form_id).filter(Boolean));
  const mandatorySatisfied = mandatoryForms.every((f) => uploadedFormIds.has(f.id));
  // TESTING BYPASS (re-enable before go-live): allow submit without full verification.
  // Original: const canSubmit = busDone && prodDone && photosDone && !["Under Review", "Approved"].includes(inst.status);
  const canSubmit = !["Under Review", "Approved"].includes(inst.status);
  const canWork = can("installations.perform");

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="installation-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t("installDetail.back")}
      </button>

      {!online && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="offline-banner">
          <Wifi className="h-4 w-4" /> {t("installDetail.offline")}
        </div>
      )}

      {/* Overview */}
      <Card className="border-slate-200 bg-white p-5 shadow-sm" data-testid="overview-card">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-black text-slate-900">{inst.bus_number || inst.vin || inst.expected_bus_serial}</h1>
          <StatusBadge status={inst.status} />
        </div>
        <div className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-3">
          <div><div className="overline text-slate-400">{t("installDetail.busNumber")}</div><div className="text-slate-900">{inst.bus_number || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.busType")}</div><div className="text-slate-900">{inst.bus_type || inst.bus_variant || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.vin")}</div><div className="text-slate-900">{inst.vin || inst.expected_bus_serial || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.manufacturer")}</div><div className="text-slate-900">{inst.manufacturer || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.energySource")}</div><div className="text-slate-900">{inst.energy_source || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.productSerial")}</div><div className="text-slate-900">{inst.expected_product_serial || t("installDetail.notSet")}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.verifiedVin")}</div><div className="text-slate-900">{inst.verified_bus_serial || "—"}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.technician")}</div><div className="text-slate-900">{inst.technician_name || t("installDetail.unassigned")}</div></div>
          <div><div className="overline text-slate-400">{t("installDetail.approvalDate")}</div><div className="text-slate-900">{inst.approval_date ? new Date(inst.approval_date).toLocaleDateString() : "—"}</div></div>
        </div>

        {/* Timer */}
        {canWork && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md bg-slate-50 p-3">
            <Clock className="h-5 w-5 text-indigo-600" />
            <span className="font-heading text-xl font-black text-slate-900" data-testid="timer-display">{fmtDuration(liveElapsed)}</span>
            {inst.timer_stopped ? (
              <span data-testid="timer-locked" className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">{t("installDetail.timeLocked", { time: fmtDuration(inst.total_seconds) })}</span>
            ) : (
              <>
                {inst.timer_running
                  ? <Button size="sm" data-testid="timer-pause" onClick={() => timer("pause")} disabled={busy} className="gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950"><Pause className="h-4 w-4" /> {t("installDetail.pause")}</Button>
                  : <Button size="sm" data-testid="timer-start" onClick={() => timer("start")} disabled={busy || inst.status === "Approved"} className="gap-1.5 bg-emerald-600 hover:bg-emerald-500"><Play className="h-4 w-4" /> {t("installDetail.start")}</Button>}
                <Button size="sm" data-testid="timer-stop" onClick={() => timer("stop")} disabled={busy || (!inst.timer_running && !(inst.total_seconds > 0))} className="gap-1.5 bg-slate-800 hover:bg-slate-700"><Square className="h-4 w-4" /> {t("installDetail.stop")}</Button>
                {inst.timer_running && <span className="text-xs text-emerald-600">{t("installDetail.tracking")}</span>}
              </>
            )}
            {user.role === "technician" && !inst.assigned_technician_id && inProjectPool && (
              <Button size="sm" variant="outline" data-testid="self-assign" onClick={selfAssign} className="ml-auto gap-1.5 border-slate-300"><UserCheck className="h-4 w-4" /> {t("installDetail.assignToMe")}</Button>
            )}
          </div>
        )}
      </Card>

      {canWork && <>
      {/* Pass A */}
      <Step n={1} title={t("installDetail.passA")} done={busDone} active={!busDone}>
        {busDone ? (
          <p className="text-sm text-emerald-600">{t("installDetail.verifiedAs", { serial: inst.verified_bus_serial, method: inst.verification_method })}</p>
        ) : (
          <div className="space-y-3">
            <Button data-testid="scan-bus-btn" onClick={() => setScan("bus")} className="h-14 w-full gap-2 bg-blue-600 text-base hover:bg-blue-500"><ScanLine className="h-5 w-5" /> {t("installDetail.scanVin")}</Button>
            {busResult && !busResult.matched && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600" data-testid="bus-mismatch">
                <XCircle className="mb-1 inline h-4 w-4" /> {t("installDetail.readMismatch", { read: busResult.extracted || "—", expected: busResult.expected })}
              </div>
            )}
            <div className="flex gap-2">
              <Input data-testid="manual-bus" placeholder={t("installDetail.manualVin")} value={manualBus} onChange={(e) => setManualBus(e.target.value)} className="border-slate-300 bg-white" />
              <Button data-testid="manual-bus-submit" onClick={() => verify("bus", null, manualBus)} disabled={busy || !manualBus} variant="outline" className="border-slate-300">{t("installDetail.verify")}</Button>
            </div>
            <Button data-testid="override-btn" onClick={requestOverride} variant="ghost" size="sm" className="gap-1.5 text-amber-600"><ShieldAlert className="h-4 w-4" /> {t("installDetail.requestOverride")}</Button>
          </div>
        )}
      </Step>

      {/* Pass B */}
      <Step n={2} title={t("installDetail.passB")} done={prodDone} active={!prodDone}>
        <div className="space-y-3">
          {(inst.product_serials || []).length > 0 && (
            <div className="space-y-2" data-testid="product-serials-list">
              {(inst.product_serials || []).map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-2.5" data-testid={`product-serial-${p.id}`}>
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{p.serial}</div>
                    {p.product_name && <div className="truncate text-xs text-slate-500">{p.product_name}</div>}
                  </div>
                  {canWork && (
                    <Button size="sm" variant="ghost" data-testid={`remove-product-serial-${p.id}`} onClick={() => removeProductSerial(p.id)} disabled={busy} className="text-red-500 hover:bg-red-50"><X className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canWork && (
            <>
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <Input data-testid="product-name-entry" placeholder={t("installDetail.productNameOptional")} value={prodName} onChange={(e) => setProdName(e.target.value)} className="border-slate-300 bg-white" />
                <div className="relative">
                  <Input data-testid="product-serial-entry" placeholder={t("installDetail.productSerialNumber")} value={prodSerialInput} onChange={(e) => setProdSerialInput(e.target.value)} className="border-slate-300 bg-white pr-11" />
                  <button
                    type="button"
                    data-testid="scan-product-btn"
                    onClick={() => setScan("product")}
                    aria-label={t("installDetail.scanBarcode")}
                    title={t("installDetail.scanBarcode")}
                    className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-blue-600 transition-colors hover:bg-blue-50"
                  >
                    <ScanLine className="h-5 w-5" />
                  </button>
                </div>
                <Button data-testid="save-product-serial" onClick={saveProductSerial} disabled={busy || !prodSerialInput.trim()} className="shrink-0 gap-1.5 bg-emerald-600 hover:bg-emerald-500"><Plus className="h-4 w-4" /> {t("installDetail.addSerial")}</Button>
              </div>
              <p className="text-xs text-slate-500">{t("installDetail.scanHintMulti")}</p>
            </>
          )}
        </div>
      </Step>

      {/* Photos */}
      <Step n={3} title={t("installDetail.mediaCapture")} done={photosDone} active={!photosDone}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {["before", "after"].map((w) => {
            const list = (w === "before" ? inst.photos_before : inst.photos_after) || [];
            const wLabel = w === "before" ? t("installDetail.before") : t("installDetail.after");
            return (
              <div key={w}>
                <div className="overline mb-2 text-slate-500">{wLabel} ({list.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {list.map((p) => (
                    <div key={p} className="relative">
                      <img src={fileUrl(p)} alt={wLabel} data-testid={`photo-${w}-thumb`} className="h-20 w-full rounded-md border border-slate-200 object-cover" />
                      <button onClick={() => removePhoto(w, p)} disabled={busy} data-testid={`remove-photo-${w}`} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button data-testid={`capture-${w}-btn`} onClick={() => setScan(w)} className="flex h-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 text-slate-400 transition-colors hover:border-blue-400 hover:text-blue-500">
                    <Camera className="h-5 w-5" /><span className="text-xs">{t("installDetail.addPhoto", { which: wLabel })}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-slate-500">{t("installDetail.photosHint")}</p>
      </Step>

      {/* Documents */}
      <Step n={4} title={t("installDetail.uploadDocuments")} done={mandatorySatisfied} active={!mandatorySatisfied}>
        {mandatoryForms.length > 0 && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="mandatory-list">
            <div className="overline mb-1 text-amber-700">{t("installDetail.requiredDocs")}</div>
            <ul className="space-y-1">
              {mandatoryForms.map((f) => {
                const ok = uploadedFormIds.has(f.id);
                return (
                  <li key={f.id} className="flex items-center gap-2" data-testid={`mandatory-${f.id}`}>
                    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-400" />}
                    <span className={ok ? "text-slate-700" : "text-red-600"}>{f.name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="space-y-2" data-testid="documents-list">
          {(inst.documents || []).map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={`document-${d.id}`}>
              <FileText className="h-5 w-5 shrink-0 text-blue-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-900">{d.label}</div>
                <div className="truncate text-xs text-slate-500">{d.file_name}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</div>
              </div>
              <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="text-slate-600"><Download className="h-4 w-4" /></Button></a>
              {canWork && (
                <Button size="sm" variant="ghost" data-testid={`remove-document-${d.id}`} onClick={() => removeDocument(d.id)} disabled={busy} className="text-red-500 hover:bg-red-50"><X className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
          {(inst.documents || []).length === 0 && <p className="text-sm text-slate-400">{t("installDetail.noDocuments")}</p>}
        </div>

        {checklistForms.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="checklist-forms">
            <div className="overline text-slate-500">{t("installDetail.digitalChecklists")}</div>
            {checklistForms.map((f) => {
              const done = uploadedFormIds.has(f.id);
              return (
                <div key={f.id} className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2.5" data-testid={`checklist-card-${f.id}`}>
                  {done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" /> : <ClipboardList className="h-5 w-5 shrink-0 text-indigo-500" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{f.name}{f.mandatory && <span className="ml-2 text-xs text-red-500">{t("forms.mandatory")}</span>}</div>
                    <div className="text-xs text-slate-500">{(f.items || []).length} {t("forms.itemsCount")}</div>
                  </div>
                  <Button size="sm" data-testid={`fill-checklist-${f.id}`} onClick={() => setChecklistForm(f)} className={done ? "gap-1.5 bg-slate-600 hover:bg-slate-500" : "gap-1.5 bg-indigo-600 hover:bg-indigo-500"}>
                    <ClipboardList className="h-4 w-4" /> {done ? t("installDetail.viewChecklist") : t("installDetail.fillChecklist")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {canWork && (
          <div className="mt-3">
            <input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*" onChange={onDocFile} className="hidden" data-testid="doc-file-input" />
            {!pendingDoc ? (
              <Button data-testid="upload-document-btn" onClick={() => docFileRef.current?.click()} variant="outline" className="gap-2 border-slate-300"><Upload className="h-4 w-4" /> {t("installDetail.uploadDocument")}</Button>
            ) : (
              <div className="space-y-3 rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="doc-classify">
                <div className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-blue-600" /> {pendingDoc.file_name}</div>
                <div>
                  <Label className="overline text-slate-500">{t("installDetail.docType")}</Label>
                  <Select value={docFormId} onValueChange={setDocFormId}>
                    <SelectTrigger data-testid="doc-type-select" className="mt-1.5 border-slate-300 bg-white"><SelectValue placeholder={t("installDetail.selectDocType")} /></SelectTrigger>
                    <SelectContent>
                      {uploadForms.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`doc-type-${f.id}`}>{f.name}{f.mandatory ? ` (${t("forms.mandatory")})` : ""}</SelectItem>)}
                      <SelectItem value="OTHERS" data-testid="doc-type-others">{t("installDetail.others")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {docFormId === "OTHERS" && (
                  <div>
                    <Label className="overline text-slate-500">{t("installDetail.docLabel")}</Label>
                    <Input data-testid="doc-label-input" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} placeholder={t("installDetail.docLabelPlaceholder")} className="mt-1.5 border-slate-300 bg-white" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button data-testid="confirm-doc-upload" onClick={confirmDocUpload} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("installDetail.uploadDocument")}</Button>
                  <Button variant="outline" onClick={() => setPendingDoc(null)} className="border-slate-300">{t("projectDetail.cancel")}</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Step>

      {/* Submit */}
      <Step n={5} title={t("installDetail.signoffRoute")} done={inst.status === "Approved"} active={canSubmit}>
        {inst.status === "Approved" ? <p className="text-sm text-emerald-600">{t("installDetail.approvedBySupervisor")}</p>
          : inst.status === "Under Review" ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-600">{t("installDetail.submittedVia", { route: inst.signoff_route })}</p>
              {inst.signoff_route === "ONSITE" && <Button data-testid="regen-qr" onClick={() => submit("ONSITE")} variant="outline" className="gap-2 border-slate-300"><QrCode className="h-4 w-4" /> {t("installDetail.regenerateQr")}</Button>}
            </div>
          ) : inst.status === "Declined" ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">{t("installDetail.declinedReason", { comments: inst.supervisor_comments })}</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button data-testid="submit-remote" onClick={() => submit("REMOTE")} disabled={!canSubmit || busy} className="h-14 gap-2 bg-indigo-600 hover:bg-indigo-500"><Send className="h-5 w-5" /> {t("installDetail.remoteReview")}</Button>
              <Button data-testid="submit-onsite" onClick={() => submit("ONSITE")} disabled={!canSubmit || busy} className="h-14 gap-2 bg-blue-600 hover:bg-blue-500"><Radio className="h-5 w-5" /> {t("installDetail.onsiteQr")}</Button>
            </div>
          )}
      </Step>
      </>}

      {/* Comments */}
      <Card className="border-slate-200 bg-white p-5 shadow-sm" data-testid="comments-panel">
        <h3 className="mb-3 font-heading font-bold text-slate-900">{t("installDetail.commentsActivity")}</h3>
        <div className="space-y-3">
          {comments.length === 0 && <p className="text-sm text-slate-400">{t("installDetail.noComments")}</p>}
          {comments.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{(c.author_name || "?").slice(0, 1)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm"><span className="font-medium text-slate-900">{c.author_name}</span> <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span></div>
                <div className="text-sm text-slate-700">{c.text}</div>
                {c.attachment_path && <img src={fileUrl(c.attachment_path)} alt="attachment" className="mt-2 max-h-40 rounded-md border border-slate-200" />}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
          {mentions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {mentions.map((m) => <span key={m.id} className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">@{m.name}</span>)}
            </div>
          )}
          <Textarea data-testid="comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder={t("installDetail.commentPlaceholder")} className="border-slate-300 bg-white" />
          {attachment && <div className="flex items-center gap-2 text-xs text-slate-500"><Paperclip className="h-3.5 w-3.5" /> {t("installDetail.attachmentReady")} <button onClick={() => setAttachment(null)} className="text-red-500">{t("installDetail.remove")}</button></div>}
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="mention-btn" className="gap-1.5 border-slate-300"><AtSign className="h-4 w-4" /> {t("installDetail.tag")}</Button>
              </PopoverTrigger>
              <PopoverContent className="max-h-64 w-56 overflow-y-auto p-1">
                {mentionable.map((u) => (
                  <button key={u.id} data-testid={`mention-${u.id}`} onClick={() => addMention(u)} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100">
                    <span>{u.name}</span><span className="text-xs text-slate-400">{u.role}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <input ref={fileRef} type="file" accept="image/*" onChange={onAttach} className="hidden" data-testid="attach-input" />
            <Button variant="outline" size="sm" data-testid="attach-btn" onClick={() => fileRef.current?.click()} className="gap-1.5 border-slate-300"><Paperclip className="h-4 w-4" /> {t("installDetail.attach")}</Button>
            <Button size="sm" data-testid="post-comment" onClick={postComment} disabled={busy} className="ml-auto bg-blue-600 hover:bg-blue-500">{t("installDetail.comment")}</Button>
          </div>
        </div>
      </Card>

      {scan && ["bus", "product"].includes(scan) && (
        <CameraCapture mode="scan" onClose={() => setScan(null)} onCapture={(d) => scan === "bus" ? verify("bus", d) : scanProductSerial(d)} valid={scan === "bus" ? busResult?.matched : undefined} />
      )}
      {scan && ["before", "after"].includes(scan) && (
        <CameraCapture mode="photo" onClose={() => setScan(null)} onCapture={(d) => capturePhoto(scan, d)} />
      )}

      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent className="border-slate-200 bg-white text-center">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("installDetail.qrSignoff")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{t("installDetail.qrDesc")}</p>
          {qr && <img data-testid="qr-image" src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qr)}`} alt="QR code" className="mx-auto rounded-lg border border-slate-200 bg-white p-3" />}
          <div className={`font-heading text-2xl font-black ${ttl > 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="qr-ttl">
            {ttl > 0 ? `${Math.floor(ttl / 60)}:${String(ttl % 60).padStart(2, "0")}` : t("installDetail.expired")}
          </div>
          <p className="break-all rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">{qr}</p>
        </DialogContent>
      </Dialog>

      {checklistForm && (
        <ChecklistFill
          installationId={id}
          formId={checklistForm.id}
          open={!!checklistForm}
          onClose={() => setChecklistForm(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
